import { Button } from "primereact/button";
import logo from "../resources/logo.svg";
import FullScreenLayout from "../layouts/full-screen-layout";
import React, { useState, useRef, useEffect } from "react";
import OtpInput from "react-otp-input";
import AuthenticationService from "../services/AuthenticationService";
import AccountService from "../services/AccountService";
import { Toast } from "primereact/toast";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { setSelf } from "../util/cache";

const CODE_EXPIRY_SECONDS = 120;

export default function TwoFactorVerify() {
  const redirect = useNavigate();
  const location = useLocation();
  const toast = useRef(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(CODE_EXPIRY_SECONDS);

  const userId = location.state?.userId;
  const rememberMe = location.state?.rememberMe ?? false;

  // Redirect back to login if accessed directly without state
  useEffect(() => {
    if (!userId) {
      redirect("/login", { replace: true });
    }
  }, [userId, redirect]);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const timer = setInterval(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearInterval(timer);
  }, [secondsLeft]);

  const formatTime = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  const isExpired = secondsLeft <= 0;

  const onSubmit = () => {
    if (isExpired || code.trim().length !== 6) return;
    setLoading(true);
    AuthenticationService.verifyTwoFactor(userId, code.trim(), rememberMe)
      .then(() => AccountService.userInfo())
      .then((user) => setSelf(user))
      .then(() => redirect("/"))
      .catch((e) => {
        console.log(e, "2fa error");
        const detail = e.message === "Network Error" ? e.message : (e.response?.data?.Errors?.[0] ?? "Verification failed");
        toast.current.show({ severity: "error", summary: "Verification Failed", detail, life: 4000 });
      })
      .finally(() => setLoading(false));
  };

  const onPressEnter = (e) => {
    if (e.key === "Enter") onSubmit();
  };

  if (!userId) return null;

  return (
    <FullScreenLayout>
      <Toast ref={toast} />
      <div className="flex-auto mt-8 shadow-none border-noround w-26rem bg-white px-4 py-4 border-1 border-300">
        <div className="flex flex-column align-items-center justify-content-center border-round m-2">
          <img src={logo} alt="logo" width={120} className="flex justify-content-center" />
          <p className="text-color font-bold mt-3">Two-Factor Verification</p>
        </div>

        <p className="text-color-secondary text-sm text-center mt-0 mb-4">
          A 6-digit verification code has been sent to your registered email or phone number. Enter it below to continue.
        </p>

        <div className="field mb-4">
          <label htmlFor="code" className="block mb-2">Verification Code</label>
          <OtpInput
            value={code}
            onChange={setCode}
            numInputs={6}
            renderInput={(props) => (
              <input
                {...props}
                disabled={isExpired}
                onKeyUp={onPressEnter}
                style={{
                  width: "2.8rem",
                  height: "3rem",
                  margin: "0 0.25rem",
                  fontSize: "1.2rem",
                  textAlign: "center",
                  border: "1px solid #ced4da",
                  borderRadius: "6px",
                  outline: "none",
                  caretColor: "transparent",
                }}
              />
            )}
            containerStyle={{ justifyContent: "center" }}
            inputType="tel"
            shouldAutoFocus
          />
        </div>

        <div className={`flex justify-content-center mb-4 font-semibold ${isExpired ? "text-red-500" : secondsLeft <= 30 ? "text-orange-500" : "text-primary"}`}>
          {isExpired ? "Code expired" : `Code expires in ${formatTime(secondsLeft)}`}
        </div>

        {isExpired ? (
          <Link to="/login" replace className="p-button p-component w-full flex justify-content-center no-underline">
            Back to Login
          </Link>
        ) : (
          <Button
            label="Verify"
            className="w-full"
            disabled={code.trim().length !== 6}
            onClick={onSubmit}
            loading={loading}
          />
        )}
      </div>
    </FullScreenLayout>
  );
}
